import { useCallback, useEffect, useState } from "react"
import { getToken } from "@/lib/api"
import type { Dependency } from "@/design-system/patterns/task-detail-pickers-extra"

export interface PeekBlockingTask {
  id: string
  name: string
  /** The dependency row's own id (app_task_dependencies.id) — needed to
   *  target DELETE /api/v1/projects/{pid}/app-task-dependencies/{id}. */
  _depId: number
}

export interface TaskPeekDepsSource {
  id: string
  project_public_id?: string | null
  project_id?: number | string | null
}

/**
 * Loads the predecessor ("Dependencies") and successor ("Blocking") lists for
 * a task-detail-peek from the project-scoped app-task-dependencies endpoint.
 * Was copy-pasted (with a subtly different id-comparison each time) across 17
 * pages/widgets — extracted here so a fix lands once for every peek-opening
 * surface. See issue #2772.
 */
export function useTaskPeekDependencies(task: TaskPeekDepsSource | null | undefined) {
  const [deps, setDeps] = useState<Dependency[]>([])
  const [blocking, setBlocking] = useState<PeekBlockingTask[]>([])

  useEffect(() => {
    if (!task) { setDeps([]); setBlocking([]); return }
    const pid = task.project_public_id || (task.project_id != null ? String(task.project_id) : "")
    if (!pid) { setDeps([]); setBlocking([]); return }
    const token = getToken() || ""
    let cancelled = false
    fetch(`/api/v1/projects/${pid}/app-task-dependencies`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : { items: [] }))
      .then(d => {
        if (cancelled) return
        const items: any[] = d.items || []
        const taskId = String(task.id)
        setDeps(
          items
            .filter(dep => String(dep.target_task_id) === taskId)
            .map(dep => ({
              id: dep.source_task_id,
              name: dep.source_task_name,
              type: (dep.source_is_permit_milestone ? "permit_milestone" : dep.source_is_phase ? "phase" : "task") as "task" | "permit_milestone" | "phase",
              depType: (dep.dependency_type || "FS") as "FS" | "SS" | "FF" | "SF",
              lag: dep.lag_days || 0,
              permitStage: dep.source_is_permit_milestone ? dep.source_permit_stage : undefined,
              _depId: dep.id as number,
            })) as Dependency[]
        )
        setBlocking(
          items
            .filter(dep => String(dep.source_task_id) === taskId)
            .map(dep => ({ id: dep.target_task_id, name: dep.target_task_name, _depId: dep.id as number }))
        )
      })
      .catch(() => { if (!cancelled) { setDeps([]); setBlocking([]) } })
    return () => { cancelled = true }
    // Keyed on the task object itself (not just its id) to match every
    // caller's prior per-page effect: several pages refetch the whole task
    // after a generic field save and rely on that new reference to also
    // refresh dependencies, without changing task.id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task])

  /**
   * Deletes a dependency row by its own id (works from either side — a
   * predecessor in `deps` or a successor in `blocking` share the same
   * app_task_dependencies row) and prunes it from local state. Shared here
   * so every peek-opening surface gets working remove for free, same as the
   * hydration this hook already unifies (#2772). See #2798.
   */
  const removeDependency = useCallback(async (depId: number) => {
    if (!task) return
    const pid = task.project_public_id || (task.project_id != null ? String(task.project_id) : "")
    if (!pid) return
    const token = getToken() || ""
    await fetch(`/api/v1/projects/${pid}/app-task-dependencies/${depId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    })
    setDeps(prev => prev.filter(d => d._depId !== depId))
    setBlocking(prev => prev.filter(b => b._depId !== depId))
  }, [task])

  return { deps, blocking, setDeps, setBlocking, removeDependency }
}
